import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Copy, Check } from 'lucide-react';
import { cn } from '../lib/utils';

const API_BASE = '/api';

const LinkedInCard = ({ post, index, copyToClipboard, copiedItems }) => {
  const [hoveredSegment, setHoveredSegment] = useState(null);

  const formatText = (text) => {
    if (!text) return '';
    // Replace literal "\n" with actual newline character
    return text.replace(/\\n/g, '\n');
  };

  const segments = [
    { id: 1, text: post.script_segment_1, analysis: post.analysis_point_1, label: 'Hook / Scroll Stopper', color: 'bg-slate-100', border: 'border-slate-400', text_color: 'text-slate-800', highlight: 'bg-slate-200' },
    { id: 2, text: post.script_segment_2, analysis: post.analysis_point_2, label: 'Trigger / Context', color: 'bg-blue-50', border: 'border-blue-400', text_color: 'text-blue-900', highlight: 'bg-blue-100' },
    { id: 3, text: post.script_segment_3, analysis: post.analysis_point_3, label: 'Pain / Problem', color: 'bg-red-50', border: 'border-red-400', text_color: 'text-red-900', highlight: 'bg-red-100' },
    { id: 4, text: post.script_segment_4, analysis: post.analysis_point_4, label: 'Solution / Advice', color: 'bg-emerald-50', border: 'border-emerald-400', text_color: 'text-emerald-900', highlight: 'bg-emerald-100' },
    { id: 5, text: post.script_segment_5, analysis: post.analysis_point_5, label: 'Engagement', color: 'bg-indigo-50', border: 'border-indigo-400', text_color: 'text-indigo-900', highlight: 'bg-indigo-100' },
    { id: 6, text: post.script_segment_6, analysis: post.analysis_point_6, label: 'Hashtags / Outro', color: 'bg-gray-50', border: 'border-gray-400', text_color: 'text-gray-900', highlight: 'bg-gray-200' },
  ];

  // Join formatted segments for the copy button
  const fullScript = segments.map(s => formatText(s.text)).join('\n\n');

  return (
    <div className="bg-white rounded-3xl shadow-lg border border-gray-200 overflow-hidden mb-12">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-700 to-blue-900 px-6 md:px-8 py-6 text-white">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          <div>
            <div className="flex flex-wrap gap-3 mb-3">
              <span className="px-3 py-1 bg-white/10 backdrop-blur text-white text-xs font-bold rounded-full uppercase tracking-wider border border-white/20">
                {post.meta_template_name}
              </span>
              <span className="px-3 py-1 bg-blue-500 text-white text-xs font-bold rounded-full uppercase tracking-wider shadow-lg shadow-blue-500/30">
                {post.meta_core_emotion}
              </span>
            </div>
            <h4 className="text-2xl font-bold leading-tight">{post.script_title}</h4>
          </div>
          <button
            onClick={() => copyToClipboard(fullScript, `li-full-${index}`)}
            className="px-5 py-2.5 bg-white text-blue-900 rounded-xl font-bold hover:bg-gray-100 transition-colors flex items-center gap-2 shadow-lg flex-shrink-0 self-start"
          >
            {copiedItems[`li-full-${index}`] ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            Kopiuj post
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 divide-y lg:divide-y-0 lg:divide-x divide-gray-200">
        {/* Left Column: Post Content */}
        <div className="p-6 md:p-8 lg:col-span-7 bg-white">
          <h5 className="font-bold text-gray-900 mb-6 flex items-center gap-2 uppercase tracking-wide text-sm">
            📝 Treść Posta
          </h5>
          <div className="prose prose-lg max-w-none text-gray-800 leading-relaxed whitespace-pre-wrap font-medium font-sans">
            {segments.map((seg, i) => (
              <span
                key={i}
                onMouseEnter={() => setHoveredSegment(seg.id)}
                onMouseLeave={() => setHoveredSegment(null)}
                className={cn(
                  "relative block mb-4 p-2 rounded transition-all cursor-help border-l-4",
                  seg.color,
                  `border-${seg.border.split('-')[1]}`,
                  hoveredSegment === seg.id ? "ring-2 ring-blue-100 shadow-sm" : "border-transparent bg-transparent p-0"
                )}
                title={`${seg.label}`}
              >
                {formatText(seg.text)}
              </span>
            ))}
          </div>
        </div>

        {/* Right Column: Analysis & Strategy */}
        <div className="p-6 md:p-8 lg:col-span-5 bg-gray-50/50 space-y-4">
          <h5 className="font-bold text-gray-900 mb-6 flex items-center gap-2 uppercase tracking-wide text-sm">
            📊 Strategia & Analiza
          </h5>
          {segments.map((seg, i) => (
            <div
              key={i}
              className={cn(
                "p-4 rounded-xl border-l-4 transition-all duration-300",
                seg.border,
                "bg-white shadow-sm",
                hoveredSegment === seg.id ? "ring-2 ring-offset-2 ring-blue-200 scale-[1.02]" : "opacity-90 hover:opacity-100"
              )}
              onMouseEnter={() => setHoveredSegment(seg.id)}
              onMouseLeave={() => setHoveredSegment(null)}
            >
              <div className={cn("text-xs font-bold uppercase tracking-widest mb-1", seg.text_color)}>
                {seg.label}
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                {seg.analysis}
              </p>
            </div>
          ))}
          
          <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-100">
             <h6 className="text-xs font-bold uppercase tracking-widest mb-2 text-blue-800">Viral Hook Strategy</h6>
             <p className="text-sm text-blue-900 italic">
               "{post.analysis_point_7 || post.script_title}"
             </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export function LinkedIn() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [linkedinData, setLinkedinData] = useState(null);
  const [copiedItems, setCopiedItems] = useState({});
  const [showDebug, setShowDebug] = useState(false);

  useEffect(() => {
    const fetchLinkedInData = async () => {
      try {
        const today = new Date().toISOString().split('T')[0];
        const statusResponse = await fetch(`${API_BASE}/linkedin-status.php?date=${today}`, {
          credentials: 'include'
        });

        if (statusResponse.status === 401) {
          navigate('/');
          return;
        }

        const statusResult = await statusResponse.json();

        if (!statusResult.success || !statusResult.linkedin_key) {
          toast.error('Posty LinkedIn nie są jeszcze gotowe.');
          setLoading(false);
          return;
        }

        const { linkedin_key } = statusResult;

        const response = await fetch(`${API_BASE}/preview.php?key=${linkedin_key}`, { credentials: 'include' });
        const result = await response.json();

        if (result.success) {
          setLinkedinData(result.data);
          toast.success('Posty LinkedIn załadowane');
        } else {
          toast.error('Błąd ładowania postów');
        }
      } catch (error) {
        toast.error('Błąd pobierania postów LinkedIn');
        console.error('Error fetching linkedin:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLinkedInData();
  }, [navigate]);

  const copyToClipboard = async (text, itemId) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
      }

      setCopiedItems(prev => ({ ...prev, [itemId]: true }));
      toast.success('Skopiowano do schowka');
      setTimeout(() => {
        setCopiedItems(prev => ({ ...prev, [itemId]: false }));
      }, 2000);
    } catch (error) {
      toast.error('Błąd kopiowania');
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="flex items-center justify-center h-64">
          <div className="text-xl font-semibold text-muted-foreground">Ładowanie postów LinkedIn...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 lg:p-6 max-w-7xl">
      {/* Header */}
      <header className="mb-6 flex justify-between items-center">
        <div>
          <button onClick={() => navigate('/')} className="text-sm text-primary hover:underline mb-2 flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" />
            Powrót do Dashboard
          </button>
          <h2 className="text-3xl font-bold text-blue-800">💼 Posty LinkedIn</h2>
          <p className="text-muted-foreground mt-1">Wygenerowane posty gotowe do publikacji</p>
        </div>
        <button 
          onClick={() => setShowDebug(!showDebug)} 
          className="px-3 py-1 bg-gray-200 text-xs rounded hover:bg-gray-300 transition-colors"
        >
          🐞 Debug
        </button>
      </header>

      {/* Debug Section */}
      {showDebug && (
        <section className="mb-12 p-4 bg-slate-100 rounded-lg border border-slate-300 overflow-x-auto">
          <h3 className="font-bold mb-2">Raw Data Debug:</h3>
          <pre className="text-xs font-mono whitespace-pre-wrap break-all">
            {JSON.stringify({ 
              dataType: Array.isArray(linkedinData) ? 'Array' : typeof linkedinData,
              dataLength: Array.isArray(linkedinData) ? linkedinData.length : 'N/A',
              data: linkedinData 
            }, null, 2)}
          </pre>
        </section>
      )}

      {/* Posts Section */}
      {(() => {
        let postsArray = [];
        if (linkedinData) {
            if (Array.isArray(linkedinData)) {
                postsArray = linkedinData;
            } else if (Array.isArray(linkedinData.posts)) {
                postsArray = linkedinData.posts;
            } else if (Array.isArray(linkedinData.data)) {
                postsArray = linkedinData.data;
            }
        }

        if (!postsArray || postsArray.length === 0) return (
            <div className="text-center py-12">
              <p className="text-gray-500">Brak postów do wyświetlenia</p>
            </div>
        );

        return (
        <section>
          <div className="space-y-8">
            {postsArray.map((item, index) => {
              const post = item.output || item;
              if (!post || !post.script_title) return null;

              return (
                <LinkedInCard 
                  key={index} 
                  post={post} 
                  index={index} 
                  copyToClipboard={copyToClipboard} 
                  copiedItems={copiedItems} 
                />
              );
            })}
          </div>
        </section>
        );
      })()}
    </div>
  );
}
